import React, { useState, memo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2 } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { registerOrLogin } from '../services/api';
import { useUISettings } from '../context/UISettingsContext';

interface AuthModalProps {
  companyName: string;
  companyEmail?: string;
  onSuccess?: () => void;
  onClose?: () => void;
}

const AuthHeader = memo(({ companyName, title, subtitle }: { companyName: string; title: string; subtitle: string }) => (
  <div className="mb-7 text-center">
    <h2 className="mb-1.5 text-2xl font-semibold tracking-tight text-[var(--app-text)]">{title}</h2>
    <p className="text-xs font-medium text-[var(--app-text-muted)]">
      {subtitle} <span className="text-[var(--app-accent)]">{companyName}</span>
    </p>
  </div>
));

const normalizeAuthError = (rawMessage?: string) => {
  const message = String(rawMessage || '').trim();
  const normalized = message.toLowerCase();
  if (normalized.includes('company name')) {
    return 'Company name is required to continue.';
  }
  if (
    normalized.includes('invalid')
    || normalized.includes('input')
    || normalized.includes('email')
    || normalized.includes('format')
  ) {
    return 'Invalid input.';
  }
  return message || 'Invalid input.';
};

const classifyMagicLinkError = (error: unknown): 'validation' | 'auth' | 'network' | 'unknown' => {
  const status = (error as { status?: number } | null)?.status;
  const message = String((error as { message?: string } | null)?.message || '').toLowerCase();
  if (
    status === 400
    || message.includes('invalid')
    || message.includes('company name')
    || message.includes('email')
    || message.includes('format')
    || message.includes('required')
  ) {
    return 'validation';
  }
  if (status === 401 || status === 403) return 'auth';
  if (
    status === 0
    || status === 502
    || status === 503
    || status === 504
    || message.includes('network')
    || message.includes('fetch')
    || message.includes('timeout')
  ) {
    return 'network';
  }
  return 'unknown';
};

export default function AuthModal({ companyName, companyEmail, onClose }: AuthModalProps) {
  const [email, setEmail] = useState(companyEmail || '');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { t } = useUISettings();

  useEffect(() => {
    setEmail(companyEmail || '');
    setMessage(null);
  }, [companyEmail]);

  const isAnyLoading = emailLoading;

  const handleEmailLogin = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (isAnyLoading) return;
    if (!termsAccepted) {
      setMessage({ type: 'error', text: t('auth.termsRequired') });
      return;
    }
    setEmailLoading(true);
    setMessage(null);
    try {
      Sentry.addBreadcrumb({
        category: 'auth.magic-link',
        message: 'Magic link request started',
        level: 'info',
      });
      await registerOrLogin(companyName, email, {
        termsAccepted,
        privacyAccepted: termsAccepted,
        legalVersion: '2026-06-16-v1.0',
        acceptedAt: new Date().toISOString(),
      });
      Sentry.addBreadcrumb({
        category: 'auth.magic-link',
        message: 'Magic link request accepted',
        level: 'info',
      });
      setMessage({ type: 'success', text: t('auth.magicLinkSent') });
    } catch (error: any) {
      const kind = classifyMagicLinkError(error);
      Sentry.addBreadcrumb({
        category: 'auth.magic-link',
        message: `Magic link request failed (${kind})`,
        level: kind === 'validation' ? 'warning' : 'error',
        data: {
          kind,
          status: (error as { status?: number } | null)?.status ?? null,
        },
      });
      if (kind !== 'validation') {
        Sentry.captureException(error, {
          tags: {
            flow: 'magic-link',
            error_kind: kind,
            area: 'auth-modal',
          },
        });
      }
      setMessage({ type: 'error', text: normalizeAuthError(error?.message) });
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div data-testid="auth-modal" className="app-panel-strong relative mx-auto w-full max-w-[22rem] overflow-hidden rounded-[1.4rem] border px-5 py-6 shadow-[0_24px_52px_-18px_rgba(0,0,0,0.28)]">
      {onClose && (
        <button
          data-testid="auth-close-btn"
          onClick={() => {
            setEmail('');
            setTermsAccepted(false);
            setMessage(null);
            onClose();
          }}
          className="absolute right-7 top-6 z-50 text-[9px] font-black uppercase tracking-[0.14em] text-[var(--app-text-soft)] transition-colors hover:text-[var(--app-text)]"
        >
          {t('auth.close')}
        </button>
      )}

      <AuthHeader companyName={companyName} title={t('auth.welcome')} subtitle={t('auth.authenticatingFor')} />

      <div className="relative mb-6 flex items-center">
        <div className="flex-grow border-t border-[var(--app-border)]" />
        <span className="flex-none px-3 text-[9px] font-bold uppercase tracking-[0.14em] text-[var(--app-text-soft)]">
          {t('auth.secureEmailLogin')}
        </span>
        <div className="flex-grow border-t border-[var(--app-border)]" />
      </div>

      <form onSubmit={handleEmailLogin} className="space-y-3" autoComplete="off">
        <div className="group/input relative">
          <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--app-text-soft)] transition-colors group-focus-within/input:text-[var(--app-accent)]" />
          <input
            type="email"
            name="auth_email_input"
            data-testid="auth-email-input"
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            spellCheck={false}
            placeholder={t('auth.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isAnyLoading}
            maxLength={320}
            className="w-full rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] py-3 pl-10 pr-3 text-xs text-[var(--app-text)] outline-none transition-all focus:border-[var(--app-accent)] focus:bg-[var(--app-surface-strong)] focus:ring-4 focus:ring-indigo-500/10 disabled:opacity-50"
            required
          />
        </div>

        <div className="space-y-3 pt-1 text-[10px] leading-relaxed text-[var(--app-text-muted)]">
          <label className="flex items-start gap-2">
            <input
              data-testid="auth-terms-checkbox"
              type="checkbox"
              checked={termsAccepted}
              onChange={(event) => setTermsAccepted(event.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 rounded border-[var(--app-border)] accent-[var(--app-accent)]"
              required
            />
            <span>
              {t('auth.termsConsent')}{' '}
              <Link to="/terms" target="_blank" rel="noreferrer" className="font-bold text-[var(--app-accent)] underline-offset-2 hover:underline">
                {t('layout.terms')}
              </Link>
              {' / '}
              <Link to="/privacy" target="_blank" rel="noreferrer" className="font-bold text-[var(--app-accent)] underline-offset-2 hover:underline">
                {t('layout.privacy')}
              </Link>
            </span>
          </label>

          <p className="rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-2 text-[9px] text-[var(--app-text-soft)]">
            {t('auth.technicalAccountHint')}
          </p>

          <details className="rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-2 text-[9px] text-[var(--app-text-soft)]">
            <summary className="cursor-pointer text-[9px] font-bold uppercase tracking-[0.1em] text-[var(--app-text-muted)]">
              {t('auth.gdprNoticeTitle')}
            </summary>
            <p className="mt-2 leading-snug">{t('auth.gdprNoticeBody')}</p>
          </details>
        </div>

        <button
          type="submit"
          data-testid="auth-magic-link-btn"
          disabled={isAnyLoading || !email.includes('@') || !termsAccepted}
          className="w-full rounded-xl border-2 border-[var(--app-text)] bg-[var(--app-text)] py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--app-bg)] transition-all duration-300 hover:bg-transparent hover:text-[var(--app-text)] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-[var(--app-text)] disabled:hover:text-[var(--app-bg)]"
        >
          {emailLoading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t('auth.requestMagicLink')}
        </button>
      </form>

      {message && (
        <p className={`mt-6 text-center text-[9px] font-bold uppercase tracking-[0.14em] ${message.type === 'success' ? 'text-emerald-500' : 'text-rose-500'}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}
